import { useMutation } from '@tanstack/react-query';
import { updateQuoteV2StatusMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2 } from 'Services/Query/QuotesV2/Types';
import { useQuoteCache } from './use-quote-cache';

interface UpdateQuoteStatusVariables {
  statusId: string;
}

/**
 * Update quote status with optimistic updates
 * Only updates statusId, does not trigger full quote edit mutation
 *
 * The PUT returns the full updated quote, so its body is written straight into
 * the quote cache — no refetch needed. It's merged over the cached quote rather
 * than replacing it: the response omits fields the GET returns (`acl`,
 * `prospect`, `segment`, notes...), and losing `acl` would instantly disable
 * edit/share/export/delete everywhere they're gated on it.
 */
export const useUpdateQuoteStatus = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);

  return useMutation({
    ...updateQuoteV2StatusMutation(),
    mutationFn: ({ statusId }: UpdateQuoteStatusVariables): Promise<QuoteV2> =>
      updateQuoteV2StatusMutation().mutationFn({ quoteId, statusId }),

    onMutate: async ({ statusId }: UpdateQuoteStatusVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      cache.setQuote((old: QuoteV2 | undefined) => {
        if (!old) return old;
        return { ...old, statusId };
      });

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      const ctx = context as { previousQuote?: QuoteV2 };
      if (ctx?.previousQuote) {
        cache.setQuote(() => ctx.previousQuote);
      }
    },

    onSuccess: (updatedQuote: QuoteV2) => {
      // Defensive: if the endpoint ever stops returning the quote, fall back to
      // marking the caches stale so the next mount/focus pulls fresh data.
      if (!updatedQuote?._id) {
        cache.invalidate();
        return;
      }

      cache.setQuote(old => (old ? { ...old, ...updatedQuote } : updatedQuote));

      // Totals, itemCount and updatedAt came back on the quote body, so only
      // the items cache still needs marking stale.
      cache.invalidateItems();
    }
  });
};
